import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

type SellerAccordionRowAppearance =
  | {
      highlighted: boolean;
      containerClassName?: never;
      headerClassName?: never;
    }
  | {
      highlighted?: never;
      containerClassName: string;
      headerClassName: string;
    };

type SellerAccordionRowProps = SellerAccordionRowAppearance & {
  testId?: string;
  disabled: boolean;
  canExpand?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  children: ReactNode;
  leadingControl?: ReactNode;
  trailingControl?: ReactNode;
  belowHeader?: ReactNode;
  expandedContent: ReactNode;
};

function cardContainerClassName(highlighted: boolean): string {
  return `overflow-hidden rounded-xl border transition-all ${
    highlighted
      ? 'border-prim-300 bg-prim-50'
      : 'border-neut-border bg-neut-bg-alt'
  }`;
}

function cardHeaderClassName(disabled: boolean): string {
  return `flex items-center gap-3 px-4 py-3 transition-colors ${
    disabled ? '' : 'hover:bg-neut-50'
  }`;
}

export function SellerAccordionRow({
  testId,
  disabled,
  canExpand,
  expanded,
  onToggleExpand,
  highlighted = false,
  containerClassName,
  headerClassName,
  children,
  leadingControl,
  trailingControl,
  belowHeader,
  expandedContent,
}: Readonly<SellerAccordionRowProps>) {
  const expandable = canExpand ?? !disabled;

  return (
    <li className={containerClassName ?? cardContainerClassName(highlighted)}>
      <div className={headerClassName ?? cardHeaderClassName(disabled)}>
        {leadingControl}
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          onClick={onToggleExpand}
          aria-expanded={expandable ? expanded : undefined}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left disabled:cursor-not-allowed disabled:opacity-60"
        >
          {children}
        </button>
        {trailingControl}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={onToggleExpand}
          className={`shrink-0 cursor-pointer ${expandable ? '' : 'invisible'}`}
        >
          <ChevronDown
            size={18}
            className={`text-neut-400 transition-transform ${
              expanded ? '' : '-rotate-90'
            }`}
          />
        </button>
      </div>
      {belowHeader}
      {expandable && expanded && expandedContent}
    </li>
  );
}
